import React, { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import CircularProgress from "@mui/material/CircularProgress";

import useMutation from "../../../hooks/useMutation";
import { ADD_INTERNAL_WORK_ORDER_ITEM_MUTATION } from "../../../constants/graphql/mutations";
import useQuery from "../../../hooks/useQuery";
import useNotification from "../../../hooks/notification";
import QuantitySelector from "./QuantitySelector";

export default function AddToInternalWorkOrder({ asset, defaultValues, processNewItem }) {
  const query = useQuery();
  const { notifySuccess } = useNotification();
  const notAvailable = asset.availableQuantity <= 0;
  const isNonAllocating = notAvailable;
  const [quantity, setQuantity] = useState(notAvailable ? 0 : 1);
  const [error, setError] = useState(null);
  const internalWorkOrderId = query.get("internalWorkOrderId");
  const [addInternalWorkOrderItem, { loading }] = useMutation(
    ADD_INTERNAL_WORK_ORDER_ITEM_MUTATION
  );

  const updateQuantity = useCallback(
    value => {
      if (isNonAllocating) return;
      if (value >= 0 && value <= asset.availableQuantity) {
        setQuantity(value);
      }
    },
    [asset.availableQuantity, isNonAllocating, setQuantity]
  );

  const handleMinus = useCallback(() => updateQuantity(quantity - 1), [quantity, updateQuantity]);

  const handlePlus = useCallback(() => updateQuantity(quantity + 1), [quantity, updateQuantity]);

  const handleUpdate = useCallback(
    event => updateQuantity(event.target.value ? parseInt(event.target.value, 10) : 0),
    [quantity, updateQuantity]
  );

  const handleAddInternalWorkOrderItem = () => {
    let variables = {
      quantity,
      assetId: asset.id,
      inventoryRequestId: internalWorkOrderId,
      comment: "",
      ...defaultValues,
    };

    if (processNewItem) {
      variables = processNewItem(variables);
    }

    const input = {
      assetId: variables.assetId,
      quantity: isNonAllocating ? 0 : variables.quantity,
      nonAllocating: isNonAllocating,
    };

    return addInternalWorkOrderItem({
      variables: { internalWorkOrderId, input },
      onSuccess: ({ internalWorkOrderItem }) => {
        setError(null);
        setQuantity(asset.availableQuantity > 0 ? 1 : 0);
        notifySuccess(
          internalWorkOrderItem?.nonAllocating
            ? "Asset successfully added as non-allocating"
            : "Asset successfully added"
        );
      },
      onFailure: errors => {
        setError(errors.quantity || errors.nonAllocating || errors.asset || errors.submit);
      },
    });
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" width="100%" gap={1}>
        <QuantitySelector
          quantity={quantity}
          onMinus={handleMinus}
          onPlus={handlePlus}
          onChange={handleUpdate}
          disabled={isNonAllocating || loading}
        />
        <Button
          disabled={loading}
          size="small"
          variant="outlined"
          onClick={handleAddInternalWorkOrderItem}
          sx={{
            minWidth: 100,
            borderRadius: "25px",
            height: "36px",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {loading && <CircularProgress size={16} />}
            {isNonAllocating ? "Add Non-allocating" : "Add"}
          </Box>
        </Button>
      </Box>
      {error && (
        <Box mt={1}>
          <FormHelperText htmlFor="barcode" error={!!error}>
            {error}
          </FormHelperText>
        </Box>
      )}
    </>
  );
}
